import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const DEFAULTS = ["Carats", "Care", "Collections", "Couples", "Journal"];

export function CategoryPicker({ label, value, onChange }: { label: string; value: string[]; onChange: (v: string[]) => void }) {
  const [all, setAll] = useState<string[]>([]);
  const [add, setAdd] = useState("");
  useEffect(() => {
    Promise.all([api.get("/admin/site"), api.get("/admin/posts")]).then(([s, p]) => {
      const used = p.data.flatMap((x: any) => x.categories || (x.category ? [x.category] : []));
      setAll(Array.from(new Set<string>([...(s.data.blog_categories || DEFAULTS), ...used, ...value])).sort());
    });
  }, []);
  const toggle = (c: string) => onChange(value.includes(c) ? value.filter((x) => x !== c) : [...value, c]);
  const addCat = async () => {
    const c = add.trim().replace(/\s+/g, " ");
    if (!c) return;
    const name = all.find((x) => x.toLowerCase() === c.toLowerCase()) || c;
    const next = Array.from(new Set([...all, name])).sort();
    try {
      await api.put("/admin/site", { blog_categories: next });
      setAll(next); onChange(value.includes(name) ? value : [...value, name]); setAdd(""); toast(`Category "${name}" added`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <fieldset className="space-y-3" data-testid="res-field-categories">
      <legend className="eyebrow text-neutral-500 mb-2">{label}</legend>
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        {all.map((c) => (
          <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={value.includes(c)} onChange={() => toggle(c)} data-testid={`category-check-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            {c}{value[0] === c && <span className="text-[10px] text-neutral-500">(shown on card)</span>}
          </label>
        ))}
      </div>
      <div className="flex gap-2 max-w-sm">
        <input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCat(); } }} placeholder="New category name" maxLength={40}
          className="flex-1 border border-neutral-300 px-3 py-2 text-sm" data-testid="category-new-input" />
        <button type="button" onClick={addCat} className="border border-neutral-900 px-4 text-xs uppercase tracking-[0.15em] hover:bg-neutral-900 hover:text-white transition-colors" data-testid="category-add-button">Add</button>
      </div>
      <p className="text-[11px] text-neutral-500">The first ticked category is shown on the article card; the article appears under every ticked category on the blog.</p>
    </fieldset>
  );
}

export function FeaturedToggle({ item, onDone }: { item: any; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const on = !!item.featured;
  const flip = async () => {
    setBusy(true);
    try { await api.put(`/admin/posts/${item.id}`, { featured: !on }); toast(on ? `"${item.title}" removed from the top banner` : `"${item.title}" is now the featured banner`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <label className="inline-flex items-center gap-2 text-xs cursor-pointer" title={on ? "Untick to remove from the top banner" : "Tick to show in the top banner"}>
      <input type="checkbox" checked={on} disabled={busy} onChange={flip} aria-label={`${on ? "Unfeature" : "Feature"} ${item.title}`} data-testid={`post-featured-${item.id}`} />
      {on ? "Featured" : "—"}
    </label>
  );
}

export function LiveToggle({ item, onDone }: { item: any; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const live = item.published !== false;
  const flip = async () => {
    setBusy(true);
    try { await api.put(`/admin/posts/${item.id}`, { published: !live }); toast(live ? `"${item.title}" unpublished` : `"${item.title}" is live`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const scheduled = live && item.publish_at && new Date(item.publish_at) > new Date();
  return (
    <label className="inline-flex items-center gap-2 text-xs cursor-pointer" title={live ? "Untick to unpublish" : "Tick to publish"}>
      <input type="checkbox" checked={live} disabled={busy} onChange={flip} aria-label={`${live ? "Unpublish" : "Publish"} ${item.title}`} data-testid={`post-live-${item.id}`} />
      {scheduled ? <span className="text-amber-700" data-testid={`post-scheduled-${item.id}`}>Scheduled · {item.publish_at_local.replace("T", " ")}</span> : live ? "Live" : "Hidden"}
    </label>
  );
}

export function PreviewButtons({ item }: { item: any }) {
  const link = async () => window.location.origin + (await api.get(`/admin-x/blog/${item.id}/preview-link`)).data.path;
  const open = async () => { const w = window.open("", "_blank"); try { const u = await link(); if (w) w.location.href = u; else window.location.href = u; } catch (e) { w?.close(); toast.error(errMsg(e)); } };
  const copy = async () => {
    try { const u = await link(); await navigator.clipboard.writeText(u).then(() => toast("Private preview link copied — anyone with it can view this article"), () => window.prompt("Copy this private preview link", u)); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <button type="button" onClick={open} className="text-xs underline underline-offset-4 hover:text-[var(--c-primary)]" data-testid={`post-preview-${item.id}`}>Preview</button>
      <button type="button" onClick={copy} className="text-xs underline underline-offset-4 hover:text-[var(--c-primary)]" data-testid={`post-preview-copy-${item.id}`}>Copy preview link</button>
    </>
  );
}
